import React, { useEffect, useState } from "react";
import axios from "axios";
import { Prose } from "@/components/site/Prose";
import { useI18n } from "@/i18n/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default fallback images (used if the backend /sight-images endpoint is unreachable)
const DEFAULT_IMGS = [
  "/ottoman_fortress.png",
  "https://images.unsplash.com/photo-1721444694627-8cabea39967e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  "/al_farran_mosque.jpg",
  "/phosphate_buildings.jpg",
  "https://images.unsplash.com/photo-1509316785289-025f5b846b35?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  "https://images.unsplash.com/photo-1619096522576-50aa4d3eb277?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
];

const SIGHTS_BY_LANG = {
  en: [
    { name: "Ottoman Fortress", desc: "The seafront citadel built in 1571 by Sultan Selim II." },
    { name: "Old Town & Coral-Stone Houses", desc: "Narrow alleys and weathered facades made of fossil coral." },
    { name: "Mosque of al-Farran", desc: "A modest historic mosque used for centuries by passing pilgrims." },
    { name: "British Phosphate Buildings", desc: "Industrial-era warehouses telling another chapter of the port." },
    { name: "Wadi Hammamat Petroglyphs", desc: "Ancient rock carvings on the caravan route to the Nile." },
    { name: "Old Harbor & Dhow Boats", desc: "Small fishing dhows still rest in the harbor at sunset." },
  ],
  de: [
    { name: "Osmanische Festung", desc: "Die Hafenfestung wurde 1571 von Sultan Selim II. erbaut." },
    { name: "Altstadt & Korallenstein-Häuser", desc: "Enge Gassen und verwitterte Fassaden aus fossilem Korallenstein." },
    { name: "Al-Farran-Moschee", desc: "Eine schlichte historische Moschee, die jahrhundertelang von Pilgern genutzt wurde." },
    { name: "Britische Phosphat-Gebäude", desc: "Industrie-Lagerhäuser erzählen ein weiteres Kapitel der Hafengeschichte." },
    { name: "Wadi Hammamat Felszeichnungen", desc: "Antike Felszeichnungen entlang der Karawanenroute zum Nil." },
    { name: "Alter Hafen & Dhau-Boote", desc: "Kleine Fischer-Dhaus liegen bei Sonnenuntergang noch heute im Hafen." },
  ],
  fr: [
    { name: "Forteresse Ottomane", desc: "La citadelle côtière construite en 1571 par le sultan Selim II." },
    { name: "Vieille Ville & Maisons en Pierre Corallienne", desc: "Ruelles étroites et façades patinées en corail fossile." },
    { name: "Mosquée d'al-Farran", desc: "Une modeste mosquée historique utilisée pendant des siècles par les pèlerins." },
    { name: "Bâtiments Britanniques du Phosphate", desc: "Entrepôts industriels racontant un autre chapitre du port." },
    { name: "Pétroglyphes du Wadi Hammamat", desc: "Anciennes gravures rupestres sur la route caravanière vers le Nil." },
    { name: "Vieux Port & Boutres", desc: "De petits boutres de pêche reposent encore au port au coucher du soleil." },
  ],
  it: [
    { name: "Fortezza Ottomana", desc: "La cittadella sul mare costruita nel 1571 dal sultano Selim II." },
    { name: "Città Vecchia & Case di Pietra Corallina", desc: "Vicoli stretti e facciate logorate in corallo fossile." },
    { name: "Moschea di al-Farran", desc: "Una modesta moschea storica usata per secoli dai pellegrini di passaggio." },
    { name: "Edifici Britannici del Fosfato", desc: "Magazzini industriali che raccontano un altro capitolo del porto." },
    { name: "Petroglifi di Wadi Hammamat", desc: "Antiche incisioni rupestri sulla rotta carovaniera verso il Nilo." },
    { name: "Vecchio Porto & Imbarcazioni Dhow", desc: "Piccoli dhow da pesca riposano ancora nel porto al tramonto." },
  ],
  ru: [
    { name: "Османская крепость", desc: "Прибрежная цитадель, построенная в 1571 году султаном Селимом II." },
    { name: "Старый город и коралловые дома", desc: "Узкие переулки и обветренные фасады из окаменелого коралла." },
    { name: "Мечеть аль-Фаррана", desc: "Скромная историческая мечеть, веками используемая паломниками." },
    { name: "Британские фосфатные здания", desc: "Промышленные склады — ещё одна глава истории порта." },
    { name: "Петроглифы Вади Хаммамат", desc: "Древние наскальные рисунки на караванном пути к Нилу." },
    { name: "Старая гавань и лодки-дхоу", desc: "Небольшие рыбацкие дхоу и сегодня стоят в гавани на закате." },
  ],
  ar: [
    { name: "القلعة العثمانية", desc: "قلعة الواجهة البحرية التي بناها السلطان سليم الثاني عام 1571." },
    { name: "البلدة القديمة وبيوت الحجر المرجاني", desc: "أزقة ضيقة وواجهات مصقولة بعوامل الزمن مصنوعة من المرجان المتحجر." },
    { name: "مسجد الفران", desc: "مسجد تاريخي متواضع استخدمه الحجاج لقرون." },
    { name: "مباني الفوسفات البريطانية", desc: "مستودعات من عصر الصناعة تروي فصلاً آخر من قصة الميناء." },
    { name: "نقوش وادي حمامات", desc: "نقوش صخرية قديمة على طريق القوافل إلى النيل." },
    { name: "الميناء القديم وقوارب الداو", desc: "لا تزال قوارب الصيد الصغيرة ترسو في الميناء عند الغروب." },
  ],
  zh: [
    { name: "奥斯曼堡垒", desc: "苏丹塞利姆二世于1571年建造的海滨城堡。" },
    { name: "老城区与珊瑚石房屋", desc: "狭窄的街巷和由化石珊瑚构成的饱经风霜的立面。" },
    { name: "法兰清真寺", desc: "一座朴素的历史清真寺,几个世纪以来一直被过路朝圣者使用。" },
    { name: "英国磷酸盐建筑", desc: "工业时代的仓库,讲述港口的另一章节。" },
    { name: "哈玛玛特干谷岩刻", desc: "通往尼罗河的商队之路上的古代岩刻。" },
    { name: "老港口与单桅帆船", desc: "小型渔用单桅帆船至今仍在日落时分停泊在港口。" },
  ],
  ja: [
    { name: "オスマン要塞", desc: "1571年にスルタン・セリム2世によって建てられた海岸の城塞。" },
    { name: "旧市街とサンゴ石の家", desc: "化石サンゴで造られた風化したファサードが並ぶ細い路地。" },
    { name: "アル・ファッラン・モスク", desc: "何世紀にもわたり巡礼者に使われた、素朴な歴史的モスク。" },
    { name: "イギリス時代のリン鉱倉庫", desc: "港のもう一つの歴史を語る産業時代の倉庫群。" },
    { name: "ワディ・ハンママートの岩刻画", desc: "ナイル川への隊商路に残る古代の岩絵。" },
    { name: "旧港とダウ船", desc: "小さな漁業用ダウ船が今も夕暮れの港に停泊している。" },
  ],
  da: [
    { name: "Osmannisk fæstning", desc: "Den kystnære citadel bygget i 1571 af sultan Selim II." },
    { name: "Den gamle by & koralstenshuse", desc: "Snævre gyder og forvitrede facader bygget af fossile koraller." },
    { name: "Al-Farran-moskéen", desc: "En beskeden historisk moské brugt i århundreder af forbipasserende pilgrimme." },
    { name: "Britiske fosfatbygninger", desc: "Industrielle pakhuse, der fortæller endnu et kapitel af havnens historie." },
    { name: "Wadi Hammamat-helleristninger", desc: "Gamle klipperistninger langs karavanevejen til Nilen." },
    { name: "Gammel havn & dhow-både", desc: "Små fiskerdhow ligger stadig i havnen ved solnedgang." },
  ],
};

const HIGHLIGHTS_TITLE = {
  en: "Highlights", de: "Höhepunkte", fr: "Points forts", it: "Punti salienti",
  ru: "Главное", ar: "أبرز المعالم", zh: "亮点", ja: "ハイライト", da: "Højdepunkter",
};

export default function SightsPage() {
  const { t, lang } = useI18n();
  const sights = SIGHTS_BY_LANG[lang] || SIGHTS_BY_LANG.en;
  const hlTitle = HIGHLIGHTS_TITLE[lang] || HIGHLIGHTS_TITLE.en;
  const [imgs, setImgs] = useState(DEFAULT_IMGS);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/sight-images`).then((r) => {
      if (cancelled) return;
      const arr = r?.data?.images;
      if (Array.isArray(arr) && arr.length === 6) setImgs(arr);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <main className="min-h-screen" data-testid="sights-page">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-28 pb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-primary)] font-bold mb-4">Chapter II</p>
        <h1 className="font-display text-stone-900 text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.9]">{t("section_sights")}</h1>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16">
        <div className="lg:col-span-7">
          <Prose section="sights" fallbackTitle={t("section_sights")} />
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-3xl overflow-hidden">
            <img src={imgs[0]} alt={sights[0].name} className="w-full h-full object-cover aspect-[4/5]" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24">
        <h2 className="font-display text-3xl text-stone-900 mb-8">{hlTitle}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sights.map((s, i) => (
            <article key={i} className="group rounded-2xl overflow-hidden bg-white border border-stone-200 hover:shadow-lg transition" data-testid={`sight-${i}`}>
              <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                <img src={imgs[i]} alt={s.name} className="h-full w-full object-cover group-hover:scale-105 transition duration-700" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl text-stone-900">{s.name}</h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
